/**
 * 68.43: the radar detail (ui/radarCoverage.ts, the Lab row Radar detail or R) drawn in the command view's overlay — for each
 * radar, at each flight height the ground under what it resolves (a fill and its edge: the dropships' height faint and
 * dashed, the low one firmer), and the edge of its gun's reach at the dropships' height (the low one is the basic picture's
 * line, rendering/radarReachView.ts). The grid's samples sit on the drawn ground; each frame they
 * are projected once (one matrix, no THREE.Vector3 per node), and the shapes are contoured between them (marching squares
 * on the polar grid: the edge runs halfway between a resolved sample and an unresolved one), so the edge follows the
 * ground smoothly rather than in the cells' steps. A grid still being sampled shows its finished rings.
 */
import * as THREE from 'three';
import type { GameState, PlanetState } from '../types';
import { TEAMS } from '../constants';
import { getSurfaceRadius } from '../core/surface';
import { personSetOf, PERSON_BODY_HEIGHT } from '../core/scaleProfile';
import { coverDir, type Coverage } from '../ui/radarCoverage';

/**
 * Each grid's nodes on the drawn ground, bearing b (0 … B−1, the samples' bearings) × radius j (0 … rings + 1): j = 0 the
 * head itself, j = 1 … rings the rings' samples, j = rings + 1 a ring past the range (never resolved, so the edge closes at
 * the range). World xyz and the node's up.
 */
export interface Nodes { heightmap: Float32Array; lift: number; xyz: Float64Array; up: Float64Array }
const nodesKept = new WeakMap<Coverage, Nodes>();

export function nodesOf(state: GameState, c: Coverage, lift: number): Nodes {
  const planet = state.planets[c.planetIndex] as PlanetState;
  const kept = nodesKept.get(c);
  if (kept && kept.heightmap === planet.heightmap && kept.lift === lift) return kept;
  const B = c.bearings, J = c.rings + 2, xyz = new Float64Array(B * J * 3), up = new Float64Array(B * J * 3);
  const person = PERSON_BODY_HEIGHT * personSetOf(state).k;
  for (let b = 0; b < B; b++) for (let j = 0; j < J; j++) {
    const theta = (j === 0 ? 0 : (j - 0.5) * c.ringPeople * person) / planet.radius;
    const d = coverDir(c, (b / B) * 2 * Math.PI, theta);
    const r = getSurfaceRadius(planet, d) + lift, k = (b * J + j) * 3;
    xyz[k] = planet.position.x + d.x * r; xyz[k + 1] = planet.position.y + d.y * r; xyz[k + 2] = planet.position.z + d.z * r;
    up[k] = d.x; up[k + 1] = d.y; up[k + 2] = d.z;
  }
  const out = { heightmap: planet.heightmap, lift, xyz, up };
  nodesKept.set(c, out);
  return out;
}

const pv = new THREE.Matrix4();
let sx = new Float32Array(0), sy = new Float32Array(0), ok = new Uint8Array(0);
// a square's walk: its corners in order (b, j), (b+1, j), (b+1, j+1), (b, j+1)
const DB = [0, 1, 1, 0], DJ = [0, 0, 1, 1];

/** A grid's shapes on screen: per height (highest first) and bit, the fill and the edge; the head. Kept while the camera, the screen and the grid stand still. */
interface Built { key: Float64Array; nodes: Nodes; layers: Array<{ low: boolean; bit: number; fill: Path2D | null; edge: Path2D | null }>; head: { x: number; y: number } | null }
const builtKept = new WeakMap<Coverage, Built>();
/** The shapes rebuilt since the page loaded (the frame budget's readout: a still camera rebuilds none). */
export const radarDrawStats = { builds: 0 };

function build(state: GameState, c: Coverage, lift: number, cam: THREE.Vector3, w: number, h: number): Built | null {
  const B = c.bearings, J = c.rings + 2, H = c.heights.length;
  const ringsDone = Math.min(c.rings, Math.floor(c.done / (H * B)));
  if (ringsDone <= 0) return null;
  const nodes = nodesOf(state, c, lift);
  const e = pv.elements;
  const key = new Float64Array(19);
  for (let k = 0; k < 16; k++) key[k] = e[k];
  key[16] = w; key[17] = h; key[18] = c.done;
  const kept = builtKept.get(c);
  if (kept && kept.nodes === nodes && kept.key.every((v, k) => v === key[k])) return kept;
  const complete = ringsDone === c.rings;
  const n = B * J;
  if (sx.length < n) { sx = new Float32Array(n); sy = new Float32Array(n); ok = new Uint8Array(n); }
  let any = false;
  for (let q = 0; q < n; q++) {
    const k = q * 3, x = nodes.xyz[k], y = nodes.xyz[k + 1], z = nodes.xyz[k + 2];
    // over the limb (the ground faces away) or behind the camera: not drawn
    const toCam = (cam.x - x) * nodes.up[k] + (cam.y - y) * nodes.up[k + 1] + (cam.z - z) * nodes.up[k + 2];
    const cw = e[3] * x + e[7] * y + e[11] * z + e[15];
    if (toCam < 0 || cw <= 1e-6) { ok[q] = 0; continue; }
    sx[q] = ((e[0] * x + e[4] * y + e[8] * z + e[12]) / cw + 1) / 2 * w;
    sy[q] = (1 - (e[1] * x + e[5] * y + e[9] * z + e[13]) / cw) / 2 * h;
    ok[q] = 1; any = true;
  }
  radarDrawStats.builds++;
  const out: Built = { key, nodes, layers: [], head: null };
  builtKept.set(c, out);
  if (!any) return out;
  const node = (b: number, j: number) => (((b % B) + B) % B) * J + j;
  // squares between radius j and j + 1 whose both rings are sampled (the one past the range once the grid is complete)
  const top = complete ? J - 1 : ringsDone;
  const px: number[] = [], py: number[] = [], mid: boolean[] = [];
  // the highest band first, the low one over it
  for (let hh = H - 1; hh >= 0; hh--) {
    // a node's value: the head takes its first ring's sample on that bearing; past the range, none
    const val = (b: number, j: number, bit: number): number => {
      const i = j === 0 ? 0 : j - 1;
      if (i >= c.rings) return 0;
      return c.cells[(hh * B + (((b % B) + B) % B)) * c.rings + i] & bit ? 1 : 0;
    };
    for (const bit of [1, 2]) {
      if (bit === 2 && hh === 0) continue;   // (the low reach: the basic picture's line, in planet space)
      const fill = new Path2D(), edge = new Path2D();
      let filled = false, edged = false;
      for (let b = 0; b < B; b++) {
        // a run of squares wholly inside along the bearing is one strip; a square the edge crosses is its own piece
        let run = -1;
        const strip = (j0: number, j1: number) => {
          fill.moveTo(sx[node(b, j0)], sy[node(b, j0)]);
          for (let j = j0 + 1; j <= j1; j++) fill.lineTo(sx[node(b, j)], sy[node(b, j)]);
          for (let j = j1; j >= j0; j--) fill.lineTo(sx[node(b + 1, j)], sy[node(b + 1, j)]);
          fill.closePath();
          filled = true;
        };
        for (let j = 0; j < top; j++) {
          let inside = 0, fine = true;
          for (let k = 0; k < 4; k++) { if (!ok[node(b + DB[k], j + DJ[k])]) fine = false; inside += val(b + DB[k], j + DJ[k], bit); }
          if (fine && inside === 4) { if (run < 0) run = j; continue; }
          if (run >= 0) { if (bit === 1) strip(run, j); run = -1; }
          if (!fine || inside === 0) continue;
          px.length = 0; py.length = 0; mid.length = 0;
          for (let k = 0; k < 4; k++) {
            const a = node(b + DB[k], j + DJ[k]), k2 = (k + 1) & 3, a2 = node(b + DB[k2], j + DJ[k2]);
            const va = val(b + DB[k], j + DJ[k], bit), vb = val(b + DB[k2], j + DJ[k2], bit);
            if (va) { px.push(sx[a]); py.push(sy[a]); mid.push(false); }
            if (va !== vb) { px.push((sx[a] + sx[a2]) / 2); py.push((sy[a] + sy[a2]) / 2); mid.push(true); }
          }
          if (bit === 1) {
            fill.moveTo(px[0], py[0]);
            for (let k = 1; k < px.length; k++) fill.lineTo(px[k], py[k]);
            fill.closePath();
            filled = true;
          }
          // the edge: each step from a crossing to the next crossing
          for (let k = 0; k < px.length; k++) {
            const k2 = (k + 1) % px.length;
            if (mid[k] && mid[k2]) { edge.moveTo(px[k], py[k]); edge.lineTo(px[k2], py[k2]); edged = true; }
          }
        }
        if (run >= 0 && bit === 1) strip(run, top);
      }
      out.layers.push({ low: hh === 0, bit, fill: filled ? fill : null, edge: edged ? edge : null });
    }
  }
  const head = node(0, 0);
  if (ok[head]) out.head = { x: sx[head], y: sy[head] };
  return out;
}

/** Draw the grids on the overlay's canvas `g` (CSS pixels `w`×`h`), faded by `alpha`; `lift` lifts the nodes off the ground. */
export function drawRadarCoverage(g: CanvasRenderingContext2D, state: GameState, camera: THREE.Camera, grids: readonly Coverage[], alpha: number, lift: number, w: number, h: number): void {
  if (!grids.length) return;
  camera.updateMatrixWorld();
  pv.multiplyMatrices(camera.projectionMatrix, camera.matrixWorldInverse);
  for (const c of grids) {
    const built = build(state, c, lift, camera.position, w, h);
    if (!built) continue;
    const colour = TEAMS[((c.team % TEAMS.length) + TEAMS.length) % TEAMS.length]?.primary ?? '#c8ccd8';
    for (const l of built.layers) {
      g.save();
      if (l.fill) { g.globalAlpha = alpha * (l.low ? 0.13 : 0.07); g.fillStyle = colour; g.fill(l.fill); }
      if (l.edge) {
        g.globalAlpha = alpha * (l.bit === 2 || l.low ? 0.85 : 0.55);
        g.strokeStyle = l.bit === 2 ? '#ffe2a0' : colour;
        g.lineWidth = l.low ? 1.5 : 1;
        g.lineJoin = 'round';
        if (!l.low) g.setLineDash([6, 4]);
        g.stroke(l.edge);
      }
      g.restore();
    }
  }
}

/**
 * The edge of `bit` at height index `hh` of a complete grid, in world space: segment end pairs (x, y, z) relative to the
 * head's node, contoured as the overlay's (marching squares on the polar grid, the edge halfway between samples).
 */
export function contourSegments(c: Coverage, nodes: Nodes, hh: number, bit: number): Float32Array {
  const B = c.bearings, J = c.rings + 2, out: number[] = [];
  const node = (b: number, j: number) => (((b % B) + B) % B) * J + j;
  const val = (b: number, j: number): number => {
    const i = j === 0 ? 0 : j - 1;
    if (i >= c.rings) return 0;
    return c.cells[(hh * B + (((b % B) + B) % B)) * c.rings + i] & bit ? 1 : 0;
  };
  const o = nodes.xyz, ox = o[0], oy = o[1], oz = o[2];
  const mids: number[] = [];
  for (let b = 0; b < B; b++) for (let j = 0; j < J - 1; j++) {
    let inside = 0;
    for (let k = 0; k < 4; k++) inside += val(b + DB[k], j + DJ[k]);
    if (inside === 0 || inside === 4) continue;
    // the square's walk: a crossing on each edge whose ends differ; the edge joins each crossing to the next
    mids.length = 0;
    const flags: boolean[] = [];
    for (let k = 0; k < 4; k++) {
      const k2 = (k + 1) & 3, va = val(b + DB[k], j + DJ[k]), vb = val(b + DB[k2], j + DJ[k2]);
      if (va) flags.push(false);
      if (va !== vb) {
        const a = node(b + DB[k], j + DJ[k]) * 3, a2 = node(b + DB[k2], j + DJ[k2]) * 3;
        mids.push((o[a] + o[a2]) / 2 - ox, (o[a + 1] + o[a2 + 1]) / 2 - oy, (o[a + 2] + o[a2 + 2]) / 2 - oz);
        flags.push(true);
      }
    }
    // in walk order, the crossings are every `true`; consecutive crossings (with no corner between) make a segment
    let m = 0;
    const at: number[] = [];
    for (const f of flags) { if (f) at.push(m++); else at.push(-1); }
    for (let k = 0; k < at.length; k++) {
      const a = at[k], b2 = at[(k + 1) % at.length];
      if (a >= 0 && b2 >= 0) out.push(mids[a * 3], mids[a * 3 + 1], mids[a * 3 + 2], mids[b2 * 3], mids[b2 * 3 + 1], mids[b2 * 3 + 2]);
    }
  }
  return new Float32Array(out);
}
